import React from "react";
import {
  Database,
  Cloud,
  Shield,
  RefreshCw,
  GitBranch,
  UserCog,
  Terminal,
  Package,
  MoreHorizontal,
} from "lucide-react";
import ScheduleScanPanel from "./ScheduleScanPanel";
import S3StorageSettings from "./S3StorageSettings";
import IamScanPanel from "./IamScanPanel";
import { useForm } from "react-hook-form";
import { useToaster } from "../../contexts/ToasterContext";

import DynamicBreadcrumbs from "../../components/Breadcrumbs/DynamicBreadcrumbs";

const dummyData = {
  cloudInfra: ["AWS", "Azure", "GCP"],
  saas: ["Slack", "Notion", "Figma"],
  storage: ["Dropbox", "Google Drive"],
};

const SelectionPanel = ({ label, data, selected, onChange }) => {
  const toggleSelection = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter((i) => i !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col">
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex-none">
        <h3 className="text-lg font-semibold text-gray-800">Select {label}</h3>
      </div>
      <div className="overflow-auto flex-1">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 sticky top-0 z-10">
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                Name
              </th>
              <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">
                Select
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((item) => (
              <tr
                key={item}
                className="hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 text-sm text-gray-900">{item}</td>
                <td className="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={selected.includes(item)}
                    onChange={() => toggleSelection(item)}
                    className="checkbox-purple"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ManageScansPage = () => {
  const { watch, setValue } = useForm({
    defaultValues: {
      selectedRepos: [],
      cloudInfra: [],
      saas: [],
      storage: [],
    },
  });

  const { showToast } = useToaster();

  const tabs = [
    { id: "Repositories", label: "Repositories", icon: GitBranch },
    { id: "Storage", label: "Storage", icon: Database },
    { id: "Cloud Infra", label: "Cloud Infra", icon: Cloud },
    { id: "SAAS", label: "SAAS", icon: Shield },
    { id: "Service Accounts", label: "Service Accounts", icon: UserCog },
    { id: "DevOps", label: "DevOps", icon: Terminal },
    { id: "Containers", label: "Containers", icon: Package },
    { id: "Other", label: "Other", icon: MoreHorizontal },
  ];
  const [activeTab, setActiveTab] = React.useState("Repositories");

  const handleTabClick = (tabId) => {
    const allowedTabs = ["Repositories", "Storage", "Cloud Infra"];
    if (!allowedTabs.includes(tabId)) {
      showToast("Plan Upgrade Required", "info");
    } else {
      setActiveTab(tabId);
    }
  };

  return (
    <div className="bg-white m-2 h-[calc(100vh-3rem)] flex flex-col overflow-hidden">
      <div className="mx-auto w-full h-full flex flex-col">
        {/* Breadcrumb + Page Title */}
        <div className="mb-4 space-y-2 flex-none">
          <DynamicBreadcrumbs />
          <h1 className="text-black text-2xl md:text-3xl font-bold">
            Manage Scans
          </h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="bg-gray-100 border-b border-gray-200 flex-none">
            <div className="flex overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isStorageTab = tab.id === "Storage";
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? "border-[#44087D] text-[#44087D] bg-white/50"
                        : "border-transparent text-gray-600 hover:text-[#44087D] hover:bg-white/30"
                    }`}
                    type="button"
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        isStorageTab ? "text-[#44087D]" : ""
                      }`}
                    />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6 flex-1 overflow-hidden flex flex-col">
            {activeTab === "Repositories" && (
              <div className="h-full flex flex-col">
                <ScheduleScanPanel
                  selectedRepos={watch("selectedRepos") || []}
                  onRepoChange={(repos) => setValue("selectedRepos", repos)}
                />
              </div>
            )}

            {activeTab === "Storage" && (
              <div className="h-full flex flex-col">
                <S3StorageSettings />
              </div>
            )}

            {activeTab === "SAAS" && (
              <div className="h-full flex flex-col space-y-6">
                <div className="flex-none flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      SaaS Applications
                    </h2>
                    <p className="text-gray-600 mt-1">
                      Select SaaS applications for security scanning
                    </p>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <SelectionPanel
                    label="SaaS Applications"
                    data={dummyData.saas}
                    selected={watch("saas") || []}
                    onChange={(items) => setValue("saas", items)}
                  />
                </div>
                <div className="flex-none flex justify-end">
                  <button className="px-6 py-3 bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a9a] transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Schedule Scans
                  </button>
                </div>
              </div>
            )}

            {activeTab === "Cloud Infra" && (
              <div className="h-full flex flex-col">
                <IamScanPanel />
              </div>
            )}

            {activeTab === "Service Accounts" && (
              <div className="space-y-6">
                <p className="text-gray-500">
                  Service Accounts settings coming soon...
                </p>
              </div>
            )}

            {activeTab === "DevOps" && (
              <div className="space-y-6">
                <p className="text-gray-500">DevOps settings coming soon...</p>
              </div>
            )}

            {activeTab === "Containers" && (
              <div className="space-y-6">
                <p className="text-gray-500">
                  Containers settings coming soon...
                </p>
              </div>
            )}

            {activeTab === "Other" && (
              <div className="space-y-6">
                <p className="text-gray-500">Other settings coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageScansPage;
